import type { AgeRestrictionType, Product, ProductStatus } from '@conai/shared-types';

export type ListEnvelope<T> = {
  results?: T[];
  items?: T[];
  products?: T[];
  data?: T[] | { results?: T[]; items?: T[]; products?: T[]; data?: T[] };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

export type ProductListItem = Product & {
  // Actual API response fields
  category?: string | null;           // UUID string
  category_name?: string | null;      // display name
  supplier?: string | null;           // UUID string
  supplier_name?: string | null;      // display name
  price?: string | number | null;     // selling price in list response
  cost_price?: string | number | null;
  image_urls?: string[] | null;
  short_description?: string | null;
  brand?: string | null;
  compare_at_price?: string | null;
  margin?: number | null;
  margin_percent?: number | null;
  min_stock_threshold?: number | null;
  stock_tracking_enabled?: boolean;
  tags?: string[] | null;
};

export type ProductModalMode = 'view' | 'create' | 'edit' | 'delete-confirm' | null;

export type StorePriceEntry = {
  store: string;
  price: string;
  valid_from: string;
  valid_to: string;
};

export type ProductFormData = {
  sku: string;
  name: string;
  barcode: string;
  categoryId: string;
  supplierId: string;
  brand: string;
  unit: string;
  basePrice: string;
  compareAtPrice: string;
  costPrice: string;
  /**
   * Thuế suất VAT của sản phẩm, phần trăm (sheet 2 #77).
   * Để TRỐNG = dùng mức mặc định của cửa hàng — đó là ca thường gặp, đừng
   * điền sẵn số gì vào đây.
   */
  taxRate: string;
  status: ProductStatus;
  shortDescription: string;
  description: string;
  tags: string;
  stockTrackingEnabled: boolean;
  /**
   * KHÔNG còn `minStockThreshold` ở đây (gỡ 09/09/2026). Ngưỡng cảnh báo giờ do
   * BE tính = 20% mốc tồn sau lần nhập gần nhất, không ai nhập tay được nữa —
   * để lại ô nhập là hứa với người dùng một thứ hệ thống không dùng tới.
   */
  imageUrl: string;

  // ── Hàng hạn chế 18+ ────────────────────────────────────────────────────────
  isAgeRestricted: boolean;
  /** Rỗng = chưa chọn. Chỉ có nghĩa khi `isAgeRestricted`. */
  ageRestrictionType: AgeRestrictionType | '';
  /** Để rỗng thì backend tự đặt 18. */
  minAge: string;
  /**
   * Bia được luật PL miễn trừ khỏi ngưỡng nồng độ 4,5% bất kể mạnh bao nhiêu,
   * nên KHÔNG suy ra được từ nồng độ — phải hỏi người nhập.
   */
  isBeer: boolean;
  /**
   * Nồng độ cồn theo thể tích, chuỗi 1 chữ số thập phân.
   *
   * Rỗng KHÔNG phải 0%: backend coi "chưa khai" là quá mạnh và sẽ chặn bán sản
   * phẩm ở cửa hàng tự phục vụ PL. Bắt buộc khi ALCOHOL và không phải bia.
   */
  alcoholByVolume: string;

  storePrices: StorePriceEntry[];
};

/**
 * Các field lưu boolean — `onFieldChange` truyền mọi thứ dưới dạng string nên
 * phải biết field nào cần đổi ngược lại, nếu không state sẽ giữ chuỗi 'false'
 * (truthy) và checkbox không bao giờ bỏ tích được.
 */
export const booleanFormFields: ReadonlySet<keyof ProductFormData> = new Set([
  'stockTrackingEnabled',
  'isAgeRestricted',
  'isBeer',
]);

/**
 * Ba field mà chỉ cần ĐỘNG VÀO là backend bật validate nồng độ cồn.
 *
 * Sửa giá hay tên của một sản phẩm rượu cũ (chưa khai nồng độ) không được bị
 * chặn — nên payload update chỉ gửi nhóm này khi người dùng thật sự đổi chúng.
 */
export const restrictionTriggerFields = [
  'isAgeRestricted',
  'ageRestrictionType',
  'isBeer',
  'alcoholByVolume',
] as const satisfies ReadonlyArray<keyof ProductFormData>;

export type ProductFormErrors = Partial<Record<keyof ProductFormData, string>>;
export type ProductFormTouched = Partial<Record<keyof ProductFormData, boolean>>;

export const formFieldOrder: Array<keyof ProductFormData> = [
  'sku', 'name', 'barcode', 'categoryId', 'supplierId', 'brand',
  'unit', 'basePrice', 'compareAtPrice', 'costPrice', 'status',
  'shortDescription', 'description', 'tags', 'stockTrackingEnabled',
  'imageUrl',
  'isAgeRestricted', 'ageRestrictionType', 'minAge', 'isBeer', 'alcoholByVolume',
];

export const emptyProductForm: ProductFormData = {
  sku: '',
  name: '',
  barcode: '',
  categoryId: '',
  supplierId: '',
  brand: '',
  unit: 'piece',
  basePrice: '',
  compareAtPrice: '',
  costPrice: '',
  taxRate: '',
  status: 'ACTIVE',
  shortDescription: '',
  description: '',
  tags: '',
  stockTrackingEnabled: false,
  imageUrl: '',
  isAgeRestricted: false,
  ageRestrictionType: '',
  minAge: '',
  isBeer: false,
  alcoholByVolume: '',
  storePrices: [],
};
